<script setup lang="ts">

const props = defineProps({
    modelValue: {
        type: String,
        default: null
    }
});

interface OverlayPanelInstance {
    toggle(event: MouseEvent): void;
    hide(): void;
}

const emit = defineEmits(['update:modelValue']);

const icons = [
    'info', 'info-circle', 'exclamation-circle', 'exclamation-triangle',
    'bell', 'prime', 'list', 'server', 'play', 'stop', 'pause',
    'user', 'users', 'thumbs-up', 'thumbs-down', 'align-left',
    'chart-bar', 'chart-line', 'sync'
];

const selectedIcon = ref<string | null>(null);
const overlayPanel = ref<OverlayPanelInstance | null>(null);

const toggleOverlay = (event: MouseEvent) => {
    overlayPanel.value?.toggle(event);
};

watch(() => props.modelValue, (newValue) => {
    selectedIcon.value = newValue;
}, { immediate: true });

const selectIcon = (icon: string) => {
    selectedIcon.value = icon;
    emit('update:modelValue', icon);
    overlayPanel.value?.hide();
};

const removeIcon = () => {
    selectedIcon.value = null;
    emit('update:modelValue', null);
};
</script>

<template>
    <div>
        <VLButton
            v-if="!selectedIcon"
            class="bg-gray-100 text-blue-600 border border-blue-600 w-10 h-10"
            @click="toggleOverlay"
        />
        <div v-else class="flex items-center">
            <VLButton
                :icon="`pi pi-${selectedIcon}`"
                class="bg-gray-100 text-blue-600 border border-blue-600 w-10 h-10"
                @click="toggleOverlay"
            />
            <i
                class="pi pi-trash cursor-pointer remove-icon"
                @click="removeIcon"
            />
        </div>
        <VLPopover
            ref="overlayPanel"
            :show-close-icon="true"
        >
            <div class="flex flex-wrap icon-selector">
                <div
                    v-for="icon in icons"
                    :key="icon"
                    :class="{ 'active-icon': selectedIcon === icon }"
                    class="icon-item"
                    @click="selectIcon(icon)"
                >
                    <i :class="`pi pi-${icon}`" class="text-2xl" />
                </div>
            </div>
        </VLPopover>
    </div>
</template>

<style scoped>
.icon-selector {
    width: 270px  !important;
}

.icon-item {
    margin: 2px;
    cursor: pointer;
    font-size: 34px;
    transition: transform 0.2s;
    padding: 0px 3px;
    width: 44px;
    height: 44px;
    border-radius: 5px;
    text-align: center;
}

.icon-item:hover, .active-icon {
    background-color: #DFEBFD;
    border: 1px solid #6f80e9;
}

:deep(.p-button-icon) {
    cursor: pointer;
    font-size: 27px;
    padding: 0px 3px;
    border-radius: 5px;
}

:deep(.p-button) {
    background: var(--cancel-btn-hover);
    border-color: var(--cancel-btn-hover);
    color: var(--primary-color);
}

:deep(.p-button:hover) {
    background: var(--light-grey);
    border-color: var(--light-grey);
    color: var(--primary-color);
}

.remove-icon {
    margin-left: 10px;
    font-size: 16px;
}
</style>
